import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { apiGet, apiPut } from '../../api/client';
import MediaPicker from '../../components/admin/MediaPicker';
import OptionGroup from './pageEditor/OptionGroup';
import BlockListEditor, { ButtonListFields, IconField, IconSuggestions } from './pageEditor/BlockListEditor';
import { DragHandle, SortableItem, reorder } from './pageEditor/sortable';
import { HOME_PLATFORMS, HOME_SECTION_TYPES, mergeHomeContent, newCustomHomeSection } from '../../data/homeDefaults';

const emptyHome = () => ({
  title: 'Home',
  seo: { description: '', keywords: '', url: 'https://zemaxi.com/' },
  content: mergeHomeContent(null),
});

const emptyAbout = () => ({
  title: 'About ZeMaxi',
  seo: { description: '', keywords: '', url: '' },
  content: {
    heroSubtitle: '',
    profileImage: '',
    profileName: 'ZeMaxi',
    profileTagline: '',
    profileButtons: [],
    storyHeading: 'My Story',
    storyParagraphs: [],
    skillsHeading: 'Technical Skills',
    skillsIntro: '',
    skills: [],
    blocks: [],
  },
});

const emptyStream = () => ({
  title: 'Live Stream',
  seo: { description: '', keywords: '', url: '' },
  content: {
    heroSubtitle: '',
    offlineHeading: 'Currently Offline',
    offlineText: '',
    offlineLinks: [],
    platformHeading: 'Watch Live',
    blocks: [],
  },
});

const PAGE_TABS = [
  {
    id: 'home',
    label: 'Home',
    path: '/',
    icon: 'fas fa-home',
    description: 'Hero, tool strip and the sections below it.',
    sections: [['hero', 'Hero'], ['ticker', 'Tool strip'], ['sections', 'Sections'], ['seo', 'SEO']],
  },
  {
    id: 'about',
    label: 'About',
    path: '/about',
    icon: 'fas fa-user',
    description: 'Your photo, story, skills and anything extra.',
    sections: [['header', 'Page header'], ['profile', 'Profile card'], ['story', 'Story'], ['skills', 'Skills'], ['blocks', 'Extra blocks'], ['seo', 'SEO']],
  },
  {
    id: 'stream',
    label: 'Stream Landing',
    path: '/stream',
    icon: 'fas fa-broadcast-tower',
    description: 'The /stream page people see when you are offline.',
    sections: [['header', 'Page header'], ['offline', 'Offline message'], ['blocks', 'Extra blocks'], ['seo', 'SEO']],
  },
];

const anchor = (section) => `page-edit-${section}`;
const snapshot = (page) => JSON.stringify({ title: page.title, seo: page.seo, content: page.content });

function SeoFields({ page, onChange }) {
  const seo = page.seo || {};
  const setSeo = (patch) => onChange({ ...page, seo: { ...seo, ...patch } });
  const description = seo.description || '';

  return (
    <OptionGroup
      id={anchor('seo')}
      icon="fas fa-search"
      title="SEO"
      description="How the page shows up on Google and when shared."
      defaultOpen={false}
    >
      <div className="pe-seo-preview" aria-label="Search result preview">
        <span className="pe-seo-preview__url">{seo.url || 'https://zemaxi.com'}</span>
        <strong className="pe-seo-preview__title">{page.title || 'Page title'} | ZeMaxi</strong>
        <p>{description || 'Add a description so search engines show something useful here.'}</p>
      </div>
      <label>
        Page title
        <input value={page.title || ''} onChange={(event) => onChange({ ...page, title: event.target.value })} />
      </label>
      <label>
        Description <em className={`pe-count${description.length > 160 ? ' is-over' : ''}`}>{description.length}/160</em>
        <textarea rows={3} value={description} onChange={(event) => setSeo({ description: event.target.value })} />
      </label>
      <div className="admin-grid-2">
        <label>
          Keywords
          <input value={seo.keywords || ''} placeholder="comma, separated" onChange={(event) => setSeo({ keywords: event.target.value })} />
        </label>
        <label>
          Canonical URL
          <input value={seo.url || ''} onChange={(event) => setSeo({ url: event.target.value })} />
        </label>
      </div>
    </OptionGroup>
  );
}

function ParagraphList({ listId, paragraphs, onChange, addLabel = 'Add paragraph' }) {
  return (
    <>
      {paragraphs.length === 0 && <p className="tpb-sidebar-hint">Nothing here yet.</p>}
      {paragraphs.map((paragraph, index) => (
        <SortableItem
          key={index}
          listId={listId}
          index={index}
          className="tpb-repeat-block"
          onMove={(from, to) => onChange(reorder(paragraphs, from, to))}
        >
          <div className="tpb-nested-block-head">
            <DragHandle listId={listId} index={index} />
            <strong>Paragraph {index + 1}</strong>
            <button type="button" className="admin-icon-btn danger" title="Remove paragraph" onClick={() => onChange(paragraphs.filter((_, i) => i !== index))}>
              <i className="fas fa-trash" aria-hidden="true" />
            </button>
          </div>
          <textarea
            rows={4}
            value={paragraph}
            onChange={(event) => {
              const next = [...paragraphs];
              next[index] = event.target.value;
              onChange(next);
            }}
          />
        </SortableItem>
      ))}
      <button type="button" className="btn btn-secondary tpb-add-btn" onClick={() => onChange([...paragraphs, ''])}>
        <i className="fas fa-plus" aria-hidden="true" /> {addLabel}
      </button>
    </>
  );
}

function AboutForm({ page, onChange }) {
  const content = page.content || {};
  const setContent = (patch) => onChange({ ...page, content: { ...content, ...patch } });
  const paragraphs = content.storyParagraphs || [];
  const skills = content.skills || [];
  const blocks = content.blocks || [];

  return (
    <div className="admin-form">
      <OptionGroup id={anchor('header')} icon="fas fa-heading" title="Page header" description="The line under “About Zemaxi” at the top.">
        <label>
          Subtitle
          <textarea rows={2} value={content.heroSubtitle || ''} onChange={(event) => setContent({ heroSubtitle: event.target.value })} />
        </label>
      </OptionGroup>

      <OptionGroup id={anchor('profile')} icon="fas fa-id-card" title="Profile card" description="Your photo, name and links, shown next to your story.">
        <div className="pe-profile-editor">
          <div className="pe-profile-preview">
            {content.profileImage ? <img src={content.profileImage} alt="" /> : <i className="fas fa-user" aria-hidden="true" />}
          </div>
          <div className="pe-profile-fields">
            <MediaPicker label="Photo" value={content.profileImage || ''} onChange={(url) => setContent({ profileImage: url })} />
            <p className="tpb-sidebar-hint">Shown as a 200px square (140px on phones), so a square photo of your face works best.</p>
            <div className="admin-grid-2">
              <label>
                Name
                <input value={content.profileName || ''} placeholder="ZeMaxi" onChange={(event) => setContent({ profileName: event.target.value })} />
              </label>
              <label>
                Tagline
                <input value={content.profileTagline || ''} placeholder="Streamer · Tool builder" onChange={(event) => setContent({ profileTagline: event.target.value })} />
              </label>
            </div>
          </div>
        </div>
        <p className="pe-subhead">Buttons under the photo</p>
        <ButtonListFields listId="about-profile-buttons" buttons={content.profileButtons} onChange={(profileButtons) => setContent({ profileButtons })} />
      </OptionGroup>

      <OptionGroup id={anchor('story')} icon="fas fa-book-open" title="Story" description="The main text beside your photo." badge={paragraphs.length}>
        <label>
          Heading <em className="pe-inline-hint">the last word is highlighted</em>
          <input value={content.storyHeading || ''} onChange={(event) => setContent({ storyHeading: event.target.value })} />
        </label>
        <ParagraphList listId="about-paragraphs" paragraphs={paragraphs} onChange={(storyParagraphs) => setContent({ storyParagraphs })} />
      </OptionGroup>

      <OptionGroup id={anchor('skills')} icon="fas fa-code" title="Skills" description="Small icon tiles. Remove them all to hide this part." badge={skills.length}>
        <div className="admin-grid-2">
          <label>
            Heading <em className="pe-inline-hint">last word highlighted</em>
            <input value={content.skillsHeading || ''} onChange={(event) => setContent({ skillsHeading: event.target.value })} />
          </label>
          <label>
            Intro
            <input value={content.skillsIntro || ''} onChange={(event) => setContent({ skillsIntro: event.target.value })} />
          </label>
        </div>
        <div className="pe-skill-grid">
          {skills.map((skill, index) => (
            <SortableItem
              key={index}
              listId="about-skills"
              index={index}
              className="tpb-repeat-block pe-skill"
              onMove={(from, to) => setContent({ skills: reorder(skills, from, to) })}
            >
              <div className="tpb-nested-block-head">
                <DragHandle listId="about-skills" index={index} />
                <strong>{skill.icon && <i className={skill.icon} aria-hidden="true" />} {skill.label || `Skill ${index + 1}`}</strong>
                <button type="button" className="admin-icon-btn danger" title="Remove skill" onClick={() => setContent({ skills: skills.filter((_, i) => i !== index) })}>
                  <i className="fas fa-trash" aria-hidden="true" />
                </button>
              </div>
              <label>
                Label
                <input value={skill.label || ''} onChange={(event) => {
                  const next = [...skills];
                  next[index] = { ...skill, label: event.target.value };
                  setContent({ skills: next });
                }} />
              </label>
              <IconField value={skill.icon} onChange={(icon) => {
                const next = [...skills];
                next[index] = { ...skill, icon };
                setContent({ skills: next });
              }} />
            </SortableItem>
          ))}
        </div>
        <button type="button" className="btn btn-secondary tpb-add-btn" onClick={() => setContent({ skills: [...skills, { icon: 'fas fa-star', label: '' }] })}>
          <i className="fas fa-plus" aria-hidden="true" /> Add skill
        </button>
      </OptionGroup>

      <OptionGroup id={anchor('blocks')} icon="fas fa-layer-group" title="Extra blocks" description="Anything else, shown below your skills: text, pictures, videos, timeline, FAQ…" badge={blocks.length}>
        <BlockListEditor listId="about-blocks" blocks={blocks} onChange={(next) => setContent({ blocks: next })} emptyText="Nothing below your skills yet." />
      </OptionGroup>

      <SeoFields page={page} onChange={onChange} />
    </div>
  );
}

function StreamLandingForm({ page, onChange }) {
  const content = page.content || {};
  const setContent = (patch) => onChange({ ...page, content: { ...content, ...patch } });
  const links = content.offlineLinks || [];
  const blocks = content.blocks || [];

  return (
    <div className="admin-form">
      <OptionGroup id={anchor('header')} icon="fas fa-heading" title="Page header" description="Subtitle at the top and the heading above the Twitch/YouTube buttons.">
        <label>
          Subtitle
          <textarea rows={2} value={content.heroSubtitle || ''} onChange={(event) => setContent({ heroSubtitle: event.target.value })} />
        </label>
        <label>
          Platform heading
          <input value={content.platformHeading || ''} onChange={(event) => setContent({ platformHeading: event.target.value })} />
        </label>
      </OptionGroup>
      <OptionGroup id={anchor('offline')} icon="fas fa-moon" title="Offline message" description="Shown instead of the player while you are not live.">
        <label>
          Heading
          <input value={content.offlineHeading || ''} onChange={(event) => setContent({ offlineHeading: event.target.value })} />
        </label>
        <label>
          Text
          <textarea rows={3} value={content.offlineText || ''} onChange={(event) => setContent({ offlineText: event.target.value })} />
        </label>
        <p className="pe-subhead">“In the meantime, you can …” links</p>
        {links.map((link, index) => (
          <SortableItem
            key={index}
            listId="offline-links"
            index={index}
            className="tpb-repeat-block"
            onMove={(from, to) => setContent({ offlineLinks: reorder(links, from, to) })}
          >
            <div className="tpb-nested-block-head">
              <DragHandle listId="offline-links" index={index} />
              <strong>{link.label || `Link ${index + 1}`}</strong>
              <button type="button" className="admin-icon-btn danger" title="Remove link" onClick={() => setContent({ offlineLinks: links.filter((_, i) => i !== index) })}>
                <i className="fas fa-trash" aria-hidden="true" />
              </button>
            </div>
            <div className="admin-grid-2">
              <label>
                Label
                <input value={link.label || ''} onChange={(event) => {
                  const offlineLinks = [...links];
                  offlineLinks[index] = { ...link, label: event.target.value };
                  setContent({ offlineLinks });
                }} />
              </label>
              <label>
                Path
                <input value={link.href || ''} onChange={(event) => {
                  const offlineLinks = [...links];
                  offlineLinks[index] = { ...link, href: event.target.value };
                  setContent({ offlineLinks });
                }} />
              </label>
            </div>
          </SortableItem>
        ))}
        <button type="button" className="btn btn-secondary tpb-add-btn" onClick={() => setContent({ offlineLinks: [...links, { href: '/', label: '' }] })}>
          <i className="fas fa-plus" aria-hidden="true" /> Add link
        </button>
      </OptionGroup>
      <OptionGroup id={anchor('blocks')} icon="fas fa-layer-group" title="Extra blocks" description="Shown under the Twitch/YouTube buttons." badge={blocks.length}>
        <BlockListEditor listId="stream-blocks" blocks={blocks} onChange={(next) => setContent({ blocks: next })} />
      </OptionGroup>
      <SeoFields page={page} onChange={onChange} />
    </div>
  );
}

function HomeForm({ page, onChange, products }) {
  const content = page.content || {};
  const hero = content.hero || {};
  const sections = content.sections || [];
  const tickerIds = hero.tickerProductIds || [];
  const productById = new Map(products.map((p) => [p.id, p]));
  const setHero = (patch) => onChange({ ...page, content: { ...content, hero: { ...hero, ...patch } } });
  const setSections = (next) => onChange({ ...page, content: { ...content, sections: next } });
  const setSection = (index, patch) => {
    const next = [...sections];
    next[index] = { ...sections[index], ...patch };
    setSections(next);
  };

  return (
    <div className="admin-form">
      <OptionGroup id={anchor('hero')} icon="fas fa-star" title="Hero" description="The big title at the top, the platform logos and the buttons.">
        <p className="tpb-sidebar-hint">
          The status pill shows live/offline and your newest tool update. The cards on the right are your top three
          featured tools from the Tool Catalog. Keep the title short: the highlight is drawn in your theme colour.
        </p>
        <div className="admin-grid-2">
          <label>
            Title
            <input value={hero.title || ''} onChange={(event) => setHero({ title: event.target.value })} />
          </label>
          <label>
            Highlight
            <input value={hero.highlight || ''} onChange={(event) => setHero({ highlight: event.target.value })} />
          </label>
        </div>
        <div className="admin-grid-2">
          <label>
            Logo line prefix
            <input value={hero.platformsPrefix || ''} placeholder="for" onChange={(event) => setHero({ platformsPrefix: event.target.value })} />
          </label>
          <div>
            <p className="tpb-sidebar-hint">Platform logos (third line)</p>
            {Object.entries(HOME_PLATFORMS).map(([id, label]) => (
              <label className="admin-switch" key={id}>
                <input
                  type="checkbox"
                  checked={(hero.platforms || []).includes(id)}
                  onChange={(event) => setHero({
                    platforms: Object.keys(HOME_PLATFORMS).filter((p) => (p === id ? event.target.checked : (hero.platforms || []).includes(p))),
                  })}
                />
                <span>{label}</span>
              </label>
            ))}
          </div>
        </div>
        <label>
          Subtitle (optional)
          <textarea rows={2} value={hero.subtitle || ''} onChange={(event) => setHero({ subtitle: event.target.value })} />
        </label>
        <p className="pe-subhead">Buttons</p>
        <ButtonListFields listId="home-hero-buttons" buttons={hero.buttons} onChange={(buttons) => setHero({ buttons })} />
      </OptionGroup>
      <OptionGroup id={anchor('ticker')} icon="fas fa-exchange-alt" title="Scrolling tool strip" description="The moving row of tools under the hero.">
        <label className="admin-switch">
          <input type="checkbox" checked={Boolean(hero.showTicker)} onChange={(event) => setHero({ showTicker: event.target.checked })} />
          <span>Show the strip under the hero</span>
        </label>
        {hero.showTicker && (
          <>
            <p className="tpb-sidebar-hint">
              {tickerIds.length
                ? 'Only these tools scroll, in this order. Drag to reorder.'
                : 'No tools picked, so every tool in the catalog scrolls. Add tools to choose your own.'}
            </p>
            {tickerIds.map((id, index) => {
              const product = productById.get(id);
              return (
                <SortableItem
                  key={id}
                  listId="home-ticker"
                  index={index}
                  className="tpb-repeat-block admin-ticker-item"
                  onMove={(from, to) => setHero({ tickerProductIds: reorder(tickerIds, from, to) })}
                >
                  <div className="tpb-nested-block-head">
                    <DragHandle listId="home-ticker" index={index} />
                    {product?.overlayImage && <img src={product.overlayImage} alt="" className="admin-ticker-thumb" />}
                    <strong>{product ? product.name : 'Deleted tool'}</strong>
                    <button
                      type="button"
                      className="admin-icon-btn danger"
                      title="Remove from strip"
                      onClick={() => setHero({ tickerProductIds: tickerIds.filter((other) => other !== id) })}
                    >
                      <i className="fas fa-times" aria-hidden="true" />
                    </button>
                  </div>
                </SortableItem>
              );
            })}
            <div className="admin-grid-2">
              <label>
                Add tool
                <select
                  value=""
                  onChange={(event) => event.target.value && setHero({ tickerProductIds: [...tickerIds, event.target.value] })}
                >
                  <option value="">Choose a tool…</option>
                  {products.filter((p) => !tickerIds.includes(p.id)).map((p) => (
                    <option key={p.id} value={p.id}>{p.name}</option>
                  ))}
                </select>
              </label>
              {tickerIds.length > 0 && (
                <button type="button" className="btn btn-secondary tpb-add-btn" onClick={() => setHero({ tickerProductIds: [] })}>
                  Reset to all tools
                </button>
              )}
            </div>
          </>
        )}
      </OptionGroup>
      <OptionGroup
        id={anchor('sections')}
        icon="fas fa-bars"
        title="Sections"
        description="Everything below the hero, top to bottom. Drag to reorder, or add your own."
        badge={sections.filter((s) => s.enabled).length}
      >
        <p className="tpb-sidebar-hint">
          Featured tools come from the Tool Catalog (★ and order), updates from Tool Updates, and the spotlight video
          from Videos. Custom sections are yours to fill with any blocks.
        </p>
        {sections.map((section, index) => {
          const type = section.custom
            ? { label: 'Custom section', icon: 'fas fa-layer-group' }
            : HOME_SECTION_TYPES[section.id] || {};
          return (
            <SortableItem
              key={section.id}
              listId="home-sections"
              index={index}
              className={`tpb-repeat-block${section.enabled ? '' : ' is-disabled'}${section.custom ? ' pe-custom-section' : ''}`}
              onMove={(from, to) => setSections(reorder(sections, from, to))}
            >
              <div className="tpb-nested-block-head">
                <DragHandle listId="home-sections" index={index} />
                <strong>
                  <i className={type.icon} aria-hidden="true" /> {type.label}
                  {section.custom && section.heading && <span className="pe-muted"> · {[section.heading, section.highlight].filter(Boolean).join(' ')}</span>}
                </strong>
                <label className="admin-switch">
                  <input type="checkbox" checked={Boolean(section.enabled)} onChange={(event) => setSection(index, { enabled: event.target.checked })} />
                  <span>Show</span>
                </label>
                {section.custom && (
                  <button
                    type="button"
                    className="admin-icon-btn danger pe-section-delete"
                    title="Delete custom section"
                    onClick={() => window.confirm('Delete this custom section and its blocks?') && setSections(sections.filter((_, i) => i !== index))}
                  >
                    <i className="fas fa-trash" aria-hidden="true" />
                  </button>
                )}
              </div>
              {section.enabled && (
                <>
                  <div className="admin-grid-2">
                    <label>
                      Eyebrow <em className="pe-inline-hint">small mono line</em>
                      <input value={section.eyebrow || ''} placeholder="// MY_SECTION" onChange={(event) => setSection(index, { eyebrow: event.target.value })} />
                    </label>
                    {type.hasCount && (
                      <label>
                        Items to show
                        <input type="number" min={1} max={24} value={section.count || 1} onChange={(event) => setSection(index, { count: Number(event.target.value) })} />
                      </label>
                    )}
                    <label>
                      Heading
                      <input value={section.heading || ''} onChange={(event) => setSection(index, { heading: event.target.value })} />
                    </label>
                    <label>
                      Highlight
                      <input value={section.highlight || ''} onChange={(event) => setSection(index, { highlight: event.target.value })} />
                    </label>
                  </div>
                  <label>
                    Intro
                    <textarea rows={2} value={section.intro || ''} onChange={(event) => setSection(index, { intro: event.target.value })} />
                  </label>
                  {section.id === 'cta' && (
                    <ButtonListFields listId="home-cta-buttons" buttons={section.buttons} onChange={(buttons) => setSection(index, { buttons })} />
                  )}
                  {section.custom && (
                    <BlockListEditor
                      listId={`home-${section.id}`}
                      blocks={section.blocks}
                      onChange={(blocks) => setSection(index, { blocks })}
                      emptyText="This section has no blocks yet."
                    />
                  )}
                </>
              )}
            </SortableItem>
          );
        })}
        <button type="button" className="btn btn-secondary tpb-add-btn" onClick={() => setSections([...sections, newCustomHomeSection()])}>
          <i className="fas fa-plus" aria-hidden="true" /> Add custom section
        </button>
      </OptionGroup>
      <SeoFields page={page} onChange={onChange} />
    </div>
  );
}

export default function AdminPages() {
  const [activeTab, setActiveTab] = useState('home');
  const [editMode, setEditMode] = useState('form');
  const [home, setHome] = useState(emptyHome());
  const [about, setAbout] = useState(emptyAbout());
  const [stream, setStream] = useState(emptyStream());
  const [saved, setSaved] = useState({});
  const [products, setProducts] = useState([]);
  const [jsonText, setJsonText] = useState('');
  const [message, setMessage] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    apiGet('/products').then(setProducts).catch(console.error);
    apiGet('/pages/home')
      .then((homePage) => {
        const next = { ...emptyHome(), ...homePage, content: mergeHomeContent(homePage.content) };
        setHome(next);
        setSaved((s) => ({ ...s, home: snapshot(next) }));
      })
      .catch(() => setSaved((s) => ({ ...s, home: snapshot(emptyHome()) })));
    Promise.all([apiGet('/pages/about'), apiGet('/pages/stream')])
      .then(([aboutPage, streamPage]) => {
        const nextAbout = { ...emptyAbout(), ...aboutPage, content: { ...emptyAbout().content, ...(aboutPage.content || {}) } };
        const nextStream = { ...emptyStream(), ...streamPage, content: { ...emptyStream().content, ...(streamPage.content || {}) } };
        setAbout(nextAbout);
        setStream(nextStream);
        setSaved((s) => ({ ...s, about: snapshot(nextAbout), stream: snapshot(nextStream) }));
      })
      .catch(console.error);
  }, []);

  const pages = {
    home: [home, setHome],
    about: [about, setAbout],
    stream: [stream, setStream],
  };
  const [page, setPage] = pages[activeTab];
  const activeMeta = PAGE_TABS.find((tab) => tab.id === activeTab);
  const isDirty = (id) => saved[id] !== undefined && snapshot(pages[id][0]) !== saved[id];
  const activeDirty = isDirty(activeTab) || (editMode === 'json' && jsonText !== JSON.stringify(page, null, 2));
  const anyDirty = PAGE_TABS.some((tab) => isDirty(tab.id)) || activeDirty;

  useEffect(() => {
    if (!anyDirty) return undefined;
    const warn = (event) => {
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [anyDirty]);

  const switchTab = (id) => {
    if (editMode === 'json' && activeDirty && !window.confirm('You have JSON edits that are not applied. Leave them?')) return;
    setActiveTab(id);
    setEditMode('form');
    setMessage(null);
  };

  const showJson = () => {
    setJsonText(JSON.stringify(page, null, 2));
    setEditMode('json');
    setMessage(null);
  };

  const applyJson = () => {
    try {
      setPage(JSON.parse(jsonText));
      setEditMode('form');
      setMessage({ text: 'JSON applied. Press Save to publish it.' });
    } catch (err) {
      setMessage({ text: `Invalid JSON: ${err.message}`, error: true });
    }
  };

  const discard = () => {
    if (!saved[activeTab] || !window.confirm(`Throw away your unsaved changes to ${activeMeta.label}?`)) return;
    setPage({ ...page, ...JSON.parse(saved[activeTab]) });
    setEditMode('form');
    setMessage({ text: 'Changes discarded.' });
  };

  const saveActive = async () => {
    if (saving) return;
    setMessage(null);
    setSaving(true);
    try {
      const current = editMode === 'json' ? JSON.parse(jsonText) : page;
      if (editMode === 'json') setPage(current);
      await apiPut(`/pages/${activeTab}`, {
        title: current.title,
        seo: current.seo,
        content: current.content,
      });
      setSaved((s) => ({ ...s, [activeTab]: snapshot(current) }));
      if (editMode === 'json') setJsonText(JSON.stringify(current, null, 2));
      setMessage({ text: `${activeMeta.label} page saved and live.` });
    } catch (err) {
      setMessage({ text: err.message, error: true });
    } finally {
      setSaving(false);
    }
  };

  const saveRef = useRef(saveActive);
  saveRef.current = saveActive;
  const onKeyDown = useCallback((event) => {
    if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 's') {
      event.preventDefault();
      saveRef.current();
    }
  }, []);
  useEffect(() => {
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [onKeyDown]);

  const jumpTo = (section) => {
    if (editMode !== 'form') setEditMode('form');
    requestAnimationFrame(() => document.getElementById(anchor(section))?.scrollIntoView({ behavior: 'smooth', block: 'start' }));
  };

  return (
    <div className="admin-panel-page pe">
      <IconSuggestions />
      <div className="pe-layout">
        <aside className="pe-nav">
          <p className="pe-nav-label">Pages</p>
          {PAGE_TABS.map((tab) => (
            <button
              key={tab.id}
              type="button"
              className={`pe-page-card${activeTab === tab.id ? ' is-active' : ''}`}
              onClick={() => switchTab(tab.id)}
            >
              <span className="pe-page-card__icon"><i className={tab.icon} aria-hidden="true" /></span>
              <span className="pe-page-card__text">
                <strong>{tab.label} {isDirty(tab.id) && <span className="pe-dirty-dot" title="Unsaved changes" />}</strong>
                <small>{tab.description}</small>
              </span>
            </button>
          ))}

          <p className="pe-nav-label">On this page</p>
          <nav className="pe-jump">
            {activeMeta.sections.map(([id, label], index) => (
              <button key={id} type="button" onClick={() => jumpTo(id)}>
                <span>{index + 1}</span> {label}
              </button>
            ))}
          </nav>

          <div className="pe-tip">
            <i className="fas fa-lightbulb" aria-hidden="true" />
            <p>Nothing goes live until you press <strong>Save</strong> (or Ctrl+S). Open a section, edit, then save.</p>
          </div>
        </aside>

        <div className="pe-main">
          <div className="pe-header">
            <div className="pe-header__title">
              <span className="pe-header__icon"><i className={activeMeta.icon} aria-hidden="true" /></span>
              <div>
                <h2>{activeMeta.label}</h2>
                <a href={activeMeta.path} target="_blank" rel="noopener noreferrer">zemaxi.com{activeMeta.path}</a>
              </div>
              <span className={`pe-status${activeDirty ? ' is-dirty' : ''}`}>
                {saving ? 'Saving…' : activeDirty ? 'Unsaved changes' : 'All saved'}
              </span>
            </div>
            <div className="pe-header__actions">
              <div className="pe-segmented" role="group" aria-label="Editor mode">
                <button type="button" className={editMode === 'form' ? 'is-active' : ''} onClick={() => setEditMode('form')}>
                  <i className="fas fa-sliders-h" aria-hidden="true" /> Editor
                </button>
                <button type="button" className={editMode === 'json' ? 'is-active' : ''} onClick={showJson}>
                  <i className="fas fa-code" aria-hidden="true" /> JSON
                </button>
              </div>
              <a href={activeMeta.path} target="_blank" rel="noopener noreferrer" className="btn btn-secondary">
                <i className="fas fa-external-link-alt" aria-hidden="true" /> View page
              </a>
              <button type="button" className="btn btn-primary" onClick={saveActive} disabled={saving}>
                <i className="fas fa-save" aria-hidden="true" /> {saving ? 'Saving…' : 'Save'}
              </button>
            </div>
          </div>

          {activeTab === 'stream' && (
            <div className="admin-notice-banner">
              <p>
                Schedule, Stream Info, Setup, and custom pages are edited in{' '}
                <Link to="/admin/stream-pages">Stream Pages</Link>.
                Game calendar entries are managed in <Link to="/admin/schedule">Schedule Games</Link>.
              </p>
            </div>
          )}

          {message && <p className={`admin-message${message.error ? ' admin-message--error' : ''}`}>{message.text}</p>}

          {editMode === 'json' ? (
            <div className="admin-json-panel">
              <div className="admin-json-panel-head">
                <h3>JSON <span className="pe-muted">for copying or bulk edits</span></h3>
                <button type="button" className="btn btn-primary" onClick={applyJson}>Apply JSON</button>
              </div>
              <textarea className="admin-json" value={jsonText} onChange={(event) => setJsonText(event.target.value)} rows={24} spellCheck={false} />
            </div>
          ) : (
            <div className="pe-groups">
              {activeTab === 'home' && <HomeForm page={home} onChange={setHome} products={products} />}
              {activeTab === 'about' && <AboutForm page={about} onChange={setAbout} />}
              {activeTab === 'stream' && <StreamLandingForm page={stream} onChange={setStream} />}
            </div>
          )}
        </div>
      </div>

      {activeDirty && (
        <div className="pe-savebar" role="status">
          <span><i className="fas fa-circle" aria-hidden="true" /> Unsaved changes on <strong>{activeMeta.label}</strong></span>
          <button type="button" className="btn btn-secondary" onClick={discard}>Discard</button>
          <button type="button" className="btn btn-primary" onClick={saveActive} disabled={saving}>
            {saving ? 'Saving…' : 'Save changes'}
          </button>
        </div>
      )}
    </div>
  );
}
